import { useMemo } from 'react';
import { Avatar, Flex, Typography, theme } from 'antd';
import { Line } from 'recharts';
import TimeSeriesLineChart from '@/components/charts/TimeSeriesLineChart';
import type { DeviceDownloadsResponse } from '@/types/api/devices';
import { checkboxColors, createGranularityXAxisFormatter } from '@/utils';
import { formatBigNumbers } from '@/utils/numberFormattingUtils';
import { shortMonthNames } from '@/utils/dateUtils';

type DevicesChartDataPoint = { date: string; [device: string]: number | string };

interface TopDevicesModalChartEchoProps {
  graph: DeviceDownloadsResponse['graph'];
  selectedIds: string[];
  getColor: (id: string) => number | undefined;
}

const CHART_HEIGHT = 300;
const LABEL_MAX_WIDTH = 140;
const SWATCH_SIZE = 12;

const formatXAxisDate = createGranularityXAxisFormatter('day');

const buildChartData = (graph: DeviceDownloadsResponse['graph']): DevicesChartDataPoint[] => {
  const dateMap = new Map<string, DevicesChartDataPoint>();

  for (const point of graph) {
    const existingPoint = dateMap.get(point.date) ?? { date: point.date };
    existingPoint[point.device] = point.downloads;
    dateMap.set(point.date, existingPoint);
  }

  return Array.from(dateMap.values());
};

const DevicesChartTooltipContent = ({
  payload,
  selectedIds,
  getColor,
}: {
  payload: DevicesChartDataPoint;
  selectedIds: string[];
  getColor: (id: string) => number | undefined;
}) => {
  const { token } = theme.useToken();

  if (!payload?.date) return null;

  const [, month, day] = (payload.date as string).split('-');
  const formattedDate = `${shortMonthNames[parseInt(month, 10) - 1]} ${day}`;

  const inverseTextStyle = { color: token.colorTextLightSolid };

  return (
    <Flex vertical gap={token.marginXXS}>
      <Typography.Text strong style={{ ...inverseTextStyle, marginInlineEnd: token.marginLG }}>
        {formattedDate}
      </Typography.Text>
      {selectedIds.map((device) => {
        const value = payload[device];
        if (value == null) return null;
        const colorIndex = getColor(device);
        const color = colorIndex != null ? checkboxColors[colorIndex] : undefined;

        return (
          <Flex key={device} align="center" gap={token.marginXXS}>
            <Avatar size={SWATCH_SIZE} style={{ backgroundColor: color }} />
            <Typography.Text ellipsis style={{ ...inverseTextStyle, maxWidth: LABEL_MAX_WIDTH }}>
              {device}:
            </Typography.Text>
            <Typography.Text strong style={inverseTextStyle}>
              {(value as number).toLocaleString()}
            </Typography.Text>
          </Flex>
        );
      })}
    </Flex>
  );
};

const TopDevicesModalChartEcho = ({
  graph,
  selectedIds,
  getColor,
}: TopDevicesModalChartEchoProps) => {
  const { token } = theme.useToken();
  const chartData = useMemo(() => buildChartData(graph), [graph]);

  if (selectedIds.length < 1) {
    return (
      <Flex align="center" justify="center" style={{ height: CHART_HEIGHT }}>
        <Typography.Text style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
          Select devices in the table to show them in the graph.
        </Typography.Text>
      </Flex>
    );
  }

  return (
    <TimeSeriesLineChart<DevicesChartDataPoint>
      data={chartData}
      dateKey="date"
      xAxisTickFormatter={formatXAxisDate}
      yAxisFormatter={(value) => (value !== 0 ? formatBigNumbers(value) : '')}
      ariaLabel="Device downloads over time"
      tooltipContent={(payload) => (
        <DevicesChartTooltipContent
          payload={payload}
          selectedIds={selectedIds}
          getColor={getColor}
        />
      )}
      height={CHART_HEIGHT}
    >
      {selectedIds.map((device) => (
        <Line
          key={device}
          dataKey={device}
          stroke={checkboxColors[getColor(device) ?? 0]}
          dot={false}
          connectNulls
          strokeWidth={2}
          type="linear"
        />
      ))}
    </TimeSeriesLineChart>
  );
};

export default TopDevicesModalChartEcho;
